import React, { useMemo, useState, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import JsonLd from 'components/atoms/json-ld'
import Divider from '@material-ui/core/Divider'
import LinearProgress from '@material-ui/core/LinearProgress'
import { HeaderSection } from './header-section'
import { LocationSection } from './location-section'
import { HowSection } from './how-section'
import { RegistrationCard } from './registration-card'
import { RegistrationContainer } from 'components/program-marketing/registration-card'
import EventNotFound from 'components/program-marketing/event-not-found'
import Dialog from 'components/program-marketing/dialog'
import Container from 'components/program-marketing/container'
import { ContactCoachForm } from 'components/program-marketing/contact-coach'
import { getEventDates } from 'utils/marketingPages'
import { urls } from 'pages/junior-league-marketing/layout/menus'
import LeagueStandingSection from './league-standing-section'
import { useGetScheduleLabel } from 'lib/family-cup-utils/event-dates'

export const CONTACT_COACH = gql`
  mutation ContactCoachJRL($input: JRLContactCoachInput!) {
    contactCoachJRL(input: $input) {
      message
      success
    }
  }
`

export const GET_JRL_PROGRAM = gql`
  query jrlProgram($programId: ID!) {
    jrlProgram: jrlLocalLeagueProgram(programId: $programId) {
      anticipatedEnd
      anticipatedStart
      coachingFeeInCents
      collectOnline
      description
      division
      id
      events {
        id
        date
        startTime
        endTime
        eventType
      }
      maxPlayers
      membershipFeeInCents
      name
      processingDate
      programLocationId
      published
      registrationClose
      registrationOpen
      registrationStatus
      registrationOpenings
      requirements
      seasonTbd
      status
      teams {
        id
        name
        league {
          id
          name
        }
      }
      minAge
      maxAge
      coaches {
        externalId
        id
        title
        fullName
        apsStatus
        bgsStatus
      }
      programLocation {
        id
        name
        description
        coach {
          id
          name
          phone
          profilePhoto
        }
        facility {
          name
          address1
          address2
          city
          state
          zip
          latitude
          longitude
          phone
        }
        links {
          facebook
          instagram
        }
      }
    }
  }
`

const SectionDivider = () => (
  <Container style={{ paddingTop: 0, paddingBottom: 0 }}>
    <Divider />
  </Container>
)

const NotFound = () => <EventNotFound searchUrl={urls.search} />

const EventDetails = ({ setTitle }) => {
  const { programId } = useParams()
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location,
  ])

  const { data, loading, error } = useQuery(GET_JRL_PROGRAM, {
    variables: { programId },
  })

  const scheduleLabel = useGetScheduleLabel(data?.jrlProgram)

  const [activeDialog, setActiveDialog] = useState(
    searchParams.has('contactCoach') ? 'contactCoach' : null,
  )

  const viewProgramInformation = () => setActiveDialog('programInformation')
  const viewAccessCodeRequired = () => setActiveDialog('accessCode')
  const viewTeamEligibility = () => setActiveDialog('teamEligibility')
  const viewContactCoach = () => setActiveDialog('contactCoach')
  const viewAnnualMembership = () => setActiveDialog('annualMembership')
  const viewRegistrationType = () => setActiveDialog('registrationType')

  const closeActiveDialog = () => {
    setActiveDialog(null)
  }
  useEffect(() => {
    if (!loading && !error && data?.jrlProgram) {
      setTitle(data?.jrlProgram?.name)
    }
  }, [loading, error, data, setTitle])

  if (loading) return <LinearProgress color="secondary" />
  if (error || !data?.jrlProgram) return <NotFound />

  const { jrlProgram } = data

  const { programLocation, requirements, teams, coaches } = jrlProgram

  const teamsInLeague = teams.filter(team => team.league?.id)

  if (jrlProgram.status !== 'PUBLISHED') return <NotFound />

  const eventDates = getEventDates(jrlProgram.events)

  return (
    <>
      <JsonLd
        {...{
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: jrlProgram.name,
          startDate: jrlProgram.anticipatedStart,
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: jrlProgram.status,
          image:
            'https://images.ctfassets.net/56u5qdsjym8c/1yoePOUAugYt2Za7y7Zmca/394ca263cad1c11e1344efb13ed2e0e3/JRL_programs_bg.png?h=250',
          location: {
            '@type': 'Place',
            name: programLocation?.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: programLocation.facility.address1,
              addressLocality: programLocation.facility.city,
              postalCode: programLocation.facility.zip,
              addressRegion: programLocation.facility.state,
              addressCountry: 'US',
            },
          },
          description: jrlProgram.description,
          offers: {
            '@type': 'Offer',
            url: `https://www.pgajrleague.com/junior-league-events/${jrlProgram.id}`,
            price:
              jrlProgram.coachingFeeInCents / 100 +
              jrlProgram.membershipFeeInCents / 100,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
          organizer: {
            '@type': 'Person',
            name: programLocation.coach.name,
          },
        }}
      />
      <HeaderSection program={jrlProgram} scheduleLabel={scheduleLabel} />

      <RegistrationContainer started={eventDates.started}>
        <RegistrationCard
          program={jrlProgram}
          scheduleLabel={scheduleLabel}
          viewAccessCodeRequired={viewAccessCodeRequired}
          viewTeamEligibility={viewTeamEligibility}
          viewAnnualMembership={viewAnnualMembership}
          viewRegistrationType={viewRegistrationType}
        />
      </RegistrationContainer>

      <LocationSection
        description={jrlProgram.description}
        location={programLocation}
        viewProgramInformation={viewProgramInformation}
        viewContactCoach={viewContactCoach}
        requirements={requirements}
        coaches={coaches}
      />
      {teamsInLeague.length > 0 && (
        <LeagueStandingSection teams={teamsInLeague} />
      )}
      <SectionDivider />
      <HowSection />
      <Dialog
        heading={jrlProgram.name}
        open={activeDialog === 'programInformation'}
        onClose={closeActiveDialog}
        text={jrlProgram.description}
      />
      <Dialog
        heading="Access Code Required"
        open={activeDialog === 'accessCode'}
        onClose={closeActiveDialog}
        text="This registration requires an Access Code provided by the Host Professional. Return to our search page to find open registration programs."
      />
      <Dialog
        heading="Team Eligibility &amp; Requirements"
        open={activeDialog === 'teamEligibility'}
        onClose={closeActiveDialog}
      >
        <ul>
          <li>13u Division: Designed for players 13 and under.</li>
          <li>
            17u Division: Designed for players 14-17 years old. Players cannot
            be older than 17 years of age.
          </li>
        </ul>
        <p>
          Please inquire with your PGA Coach regarding their Program's specific
          age parameters.
        </p>
        <p>
          Note: Players participating in the All-Star Season as All-Stars must
          play in a local regular season program that is within 75 driving miles
          of their residence. This mileage policy is in addition to the existing
          eligibility requirements around league age and active PGA Jr. League
          participation. More information about player eligibility and All-Star
          eligibility can be found{' '}
          <a href={urls.requirements} target="_blank" rel="noopener noreferrer">
            HERE
          </a>
          .
        </p>
      </Dialog>
      <Dialog
        heading="Contact Coach"
        open={activeDialog === 'contactCoach'}
        onClose={closeActiveDialog}
      >
        <ContactCoachForm
          coachId={programLocation.coach.id}
          onCancel={closeActiveDialog}
          mutation={CONTACT_COACH}
          resultObjectName="contactCoachJRL"
        />
      </Dialog>
      <Dialog
        title="Additional Fees"
        heading="Annual Membership Cost"
        open={activeDialog === 'annualMembership'}
        onClose={closeActiveDialog}
        text={`PGA Jr. League players must pay a $${jrlProgram.membershipFeeInCents /
          100} annual membership fee called the #GameChanger Club, valid for the calendar year. Payment during the spring season covers participation in any additional PGA Jr. League events within the same calendar year.`}
        headingSecondary={'3% Processing Fee'}
        textSecondary="This fee covers the cost of processing your payment through our credit card processor and helps to support the cost of operating the platform for your use."
      />
      <Dialog
        heading="Registration Type"
        open={activeDialog === 'registrationType'}
        onClose={closeActiveDialog}
      >
        <ul>
          <li>
            <strong>Club Membership Required</strong>: This registration
            requires a Club Membership at the Facility and an Access Code
            provided by the PGA Coach.
          </li>
          <li>
            <strong>Access Code Required</strong>: This registration is open to
            the Public with restrictions and requires an Access Code provided by
            the PGA Coach. Contact the PGA Coach for more information.
          </li>
          <li>
            <strong>Open to Public</strong>: This registration is open to the
            public.
          </li>
        </ul>
      </Dialog>
    </>
  )
}

export default EventDetails
